/**
 * Design Tokens System
 * Centralized design values for consistent UI
 */

// Size scales
export const sizes = {
  xs: { padding: "px-2 py-1", text: "text-xs", height: "h-6", icon: "w-3 h-3" },
  sm: {
    padding: "px-3 py-1.5",
    text: "text-sm",
    height: "h-8",
    icon: "w-4 h-4",
  },
  md: {
    padding: "px-4 py-2",
    text: "text-sm",
    height: "h-10",
    icon: "w-5 h-5",
  },
  lg: {
    padding: "px-5 py-2.5",
    text: "text-base",
    height: "h-12",
    icon: "w-6 h-6",
  },
  xl: {
    padding: "px-6 py-3",
    text: "text-lg",
    height: "h-14",
    icon: "w-7 h-7",
  },
};

// Consistent spacing
export const spacing = {
  xs: "0.25rem", // 4px
  sm: "0.5rem", // 8px
  md: "1rem", // 16px
  lg: "1.5rem", // 24px
  xl: "2rem", // 32px
  "2xl": "3rem", // 48px
  "3xl": "4rem", // 64px
};

// Standardized shadows
export const shadows = {
  none: "shadow-none",
  sm: "shadow-sm",
  base: "shadow",
  md: "shadow-md",
  lg: "shadow-lg",
  xl: "shadow-xl",
  "2xl": "shadow-2xl",
  inner: "shadow-inner",
};

// Animation durations
export const durations = {
  instant: "0ms",
  fast: "150ms",
  normal: "300ms",
  slow: "500ms",
  slower: "700ms",
};

// Z-index scale
export const zIndex = {
  below: -1,
  base: 0,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
  notification: 80,
  spinner: 90,
  max: 99999,
};

// Breakpoints
export const breakpoints = {
  xs: "475px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

// Consistent border radius
export const borderRadius = {
  none: "rounded-none",
  sm: "rounded-sm",
  base: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
  full: "rounded-full",
};

// Loading states
export const loadingStates = {
  spinner: {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-10 h-10",
  },
  skeleton: {
    animation: "animate-pulse",
    background: "bg-gray-200 dark:bg-gray-700",
  },
};

// Error states
export const errorStates = {
  colors: {
    border: "border-red-500 dark:border-red-400",
    text: "text-red-600 dark:text-red-400",
    background: "bg-red-50 dark:bg-red-900/20",
  },
  focus: "focus:ring-red-500 dark:focus:ring-red-400",
};

// Success states
export const successStates = {
  colors: {
    border: "border-green-500 dark:border-green-400",
    text: "text-green-600 dark:text-green-400",
    background: "bg-green-50 dark:bg-green-900/20",
  },
  focus: "focus:ring-green-500 dark:focus:ring-green-400",
};

// Warning states
export const warningStates = {
  colors: {
    border: "border-amber-500 dark:border-amber-400",
    text: "text-amber-600 dark:text-amber-400",
    background: "bg-amber-50 dark:bg-amber-900/20",
  },
  focus: "focus:ring-amber-500 dark:focus:ring-amber-400",
};

// Get size classes
export const getSizeClasses = (size = "md", component = "button") => {
  const sizeConfig = sizes[size] || sizes.md;

  switch (component) {
    case "button":
      return `${sizeConfig.padding} ${sizeConfig.text}`;
    case "input":
      return `${sizeConfig.padding} ${sizeConfig.text} ${sizeConfig.height}`;
    case "icon":
      return sizeConfig.icon;
    case "spinner":
      return loadingStates.spinner[size] || loadingStates.spinner.md;
    default:
      return sizeConfig.padding;
  }
};

// Get variant classes
export const getVariantClasses = (variant = "primary", state = "default") => {
  const variants = {
    primary: {
      default:
        "bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600",
      focus: "focus:ring-indigo-500 dark:focus:ring-indigo-400",
      disabled: "disabled:bg-indigo-300 dark:disabled:bg-indigo-800",
    },
    secondary: {
      default:
        "bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600",
      focus: "focus:ring-gray-400 dark:focus:ring-gray-500",
      disabled: "disabled:bg-gray-100 dark:disabled:bg-gray-800",
    },
    danger: {
      default:
        "bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600",
      focus: "focus:ring-red-500 dark:focus:ring-red-400",
      disabled: "disabled:bg-red-300 dark:disabled:bg-red-800",
    },
    success: {
      default:
        "bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600",
      focus: "focus:ring-green-500 dark:focus:ring-green-400",
      disabled: "disabled:bg-green-300 dark:disabled:bg-green-800",
    },
    warning: {
      default:
        "bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600",
      focus: "focus:ring-amber-500 dark:focus:ring-amber-400",
      disabled: "disabled:bg-amber-300 dark:disabled:bg-amber-800",
    },
    ghost: {
      default:
        "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800",
      focus: "focus:ring-gray-400 dark:focus:ring-gray-500",
      disabled: "disabled:text-gray-400 dark:disabled:text-gray-600",
    },
    outline: {
      default:
        "bg-transparent border-2 border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800",
      focus: "focus:ring-gray-400 dark:focus:ring-gray-500",
      disabled:
        "disabled:border-gray-200 disabled:text-gray-400 dark:disabled:border-gray-700",
    },
  };

  const variantConfig = variants[variant] || variants.primary;
  return variantConfig[state] || variantConfig.default;
};

// Common component classes
export const componentClasses = {
  card: "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm",
  input:
    "w-full border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400",
  label: "block text-sm font-medium text-gray-700 dark:text-gray-300",
  error: "text-sm text-red-600 dark:text-red-400 mt-1",
  helper: "text-sm text-gray-500 dark:text-gray-400 mt-1",
};

// Export all tokens as default
export default {
  sizes,
  spacing,
  shadows,
  durations,
  zIndex,
  breakpoints,
  borderRadius,
  loadingStates,
  errorStates,
  successStates,
  warningStates,
  getSizeClasses,
  getVariantClasses,
  componentClasses,
};
